import {ElementRef, inject, Injectable, OnDestroy, OnInit} from '@angular/core';
import {catchError, finalize, takeUntil, tap} from "rxjs/operators";
import {BasketConfirm} from "../interfaces/basket-confirm";
import {BaseService} from "./base.service";
import {HttpClient, HttpContext, HttpErrorResponse} from "@angular/common/http";
import {LocalStorageService} from "./local-storage.service";
import {EMPTY, Subject} from "rxjs";
import {OrdersSuccessData} from "../interfaces/orders-success-data";
import {Wars} from "../interfaces/account-data";
import {Delivery} from "../interfaces/delivery";
import {HttpErrorNotifyService} from "./http-error-notify.service";
import {PASS_REDIRECT_RESPONSE} from "./http-interceptor.service";

// Нова Пошта не пропускає відправлення, якщо одержувача записано латиницею
// (замовлення 652474 «Тагірлі Alim», 15.09.2026), тож у доставці, що їде НП,
// ПІБ приймаємо лише кирилицею. Ті самі правила перевіряє бекенд —
// recipient_fio_error() у kernel/helpers/h_recipient_fio.php.
// Решта доставок (Мeest, Делівері) лишається зі своїм старим алфавітом.
const RECIPIENT_LATIN_PATTERN = /[A-Za-z]/;
const RECIPIENT_PART_PATTERN = /^[А-Яа-яЁёІЇЄҐіїєґ][А-Яа-яЁёІЇЄҐіїєґ'’ʼ]{1,59}(?:-[А-Яа-яЁёІЇЄҐіїєґ][А-Яа-яЁёІЇЄҐіїєґ'’ʼ]{1,59}){0,2}$/;
const RECIPIENT_PART_PATTERN_ANY = /^[A-Za-zА-Яа-яЁёІЇЄҐіїєґ][A-Za-zА-Яа-яЁёІЇЄҐіїєґ'’ʼ]{1,59}(?:-[A-Za-zА-Яа-яЁёІЇЄҐіїєґ][A-Za-zА-Яа-яЁёІЇЄҐіїєґ'’ʼ]{1,59}){0,2}$/;
// По батькові беремо тим самим алфавітом, що й сервер (recipient_fio_error):
// кирилиця, апостроф, дефіс, крапка і пробіл. Складені по батькові («Алім огли»)
// та ініціал («О.») сервер приймає, і до цієї задачі поле взагалі не перевірялось,
// тож вужчий за серверний шаблон блокував би те, що раніше проходило.
const RECIPIENT_MIDDLE_PATTERN = /^[А-Яа-яЁёІЇЄҐіїєґ][А-Яа-яЁёІЇЄҐіїєґ'’ʼ.\- ]{0,59}$/;

// Бекенд (c_basket_data.php, pay()) тримає Redis-lock на користувача 60 секунд,
// щоб той не оформив кілька однакових замовлень поспіль, і на повторний запит
// відповідає саме цим текстом. Фронт його ніяк не показував - юзер бачив лише
// лоадер і не розумів, оформилось замовлення чи ні. Текст і оформлення блоку -
// в app-order-in-progress-message.
const ORDER_IN_PROGRESS_ERROR = 'Order allready in progress.';

// Коди відповіді pay(), які екрани доставок розбирають самі (resultSendPayment,
// warehouse_weight у НП/Meest) або які вже обробив інтерцептор (редирект).
// Решту помилок pay() фронт раніше мовчки ковтав - кнопка «Відправити» просто
// нічого не робила. Усі вони повертаються ДО створення замовлення, тож
// «не оформлено» тут - точний факт, а не припущення.
const PAY_HANDLED_ERRORS = [
  '', ORDER_IN_PROGRESS_ERROR, 'empty basket', 'too many orders', 'not_enough_liters', 'warehouse_weight',
  'not_authorized', 'user_deleted', 'site_blocked',
];
const PAY_PHONE_ERRORS = ['wrong phone', 'wrong phone format'];
const PAY_FIO_ERRORS = ['empty fio', 'wrong fio'];
const PAY_ADDRESS_ERRORS = [
  'empty address', 'wrong delivery address', 'wrong delivery city', 'wrong delivery courier_num',
  'empty courier num id', 'empty delivery city id', 'empty nova posta city id', 'empty meest city id',
  'wrong np warehouse', 'wrong meest city', 'wrong meest branch',
];
// Поля, які pay() перевіряє на довжину колонок users_addresses (#471) - підпис як на формі доставки
const PAY_LONG_FIELD_LABELS = {
  np_addr_street: 'Вулиця',
  np_addr_house: 'Будинок №',
  np_addr_flat: 'Квартира №',
  delivery_addr: 'Адреса',
};

// Дропшип 3/3 (картка #868): тексти помилок pay() - їх показує і форма дропшипу
export const DROPSHIP_TTN_TOO_LONG = 'Номер ТТН - не більше 36 цифр.';

export function dropshipPlacesMismatchText(label: number, calc: number): string {
  return `Кількість місць на наклейці (${label}) не збігається з розрахунком (${calc}). Перевірте наклейку або зверніться до менеджера`;
}

@Injectable({
  providedIn: 'root'
})

export class DeliveryService extends BaseService implements OnInit, OnDestroy {
  isDestroyed: Subject<boolean> = new Subject();
  deliveryCurrent;
  delivery;
  user;
  lastAddress;
  dozakazDate: string;
  separateOrders: boolean;
  addressedDelivery: boolean;
  summaUah: number;
  declaredPrice;
  compensation: number;
  deliveryCities;
  showMessageOtgrujaem = 0;
  name = '';
  lastName = '';
  middleName = '';
  isValidDeliveryForm = false;
  wrongLastAddressPhone = false;
  deliveryDate = -1;
  deliveryHours = -1;
  deliveryHoursList: number[] = [];
  deliverySeparate = -1;
  wrongDeliverySeparate = false;
  deliveryComment = '';
  wrongEstimateOfTheCostOfTheOrder = false;
  wrongLastAddressEdrpou = false;
  isDeliveryConfirmation = false;
  liters: number;
  wrongDeliveryDates = false;
  wrongDeliveryHours = false;
  wrongLastName = false;
  errorMessageLastName = '';
  wrongName = false;
  errorMessageName = '';
  wrongMiddleName = false;
  errorMessageMiddleName = '';
  // Вмикається у доставці, що їде Новою Поштою: НП не приймає одержувача латиницею
  recipientNameCyrillicOnly = false;
  phoneRef: ElementRef<HTMLInputElement>;
  deliveryList: Delivery[];
  wars: Wars[];
  summaUahWithMarkup: number;
  sumMarkupNalojka: number;
  reserveInfoMessageShow = false;
  // Поки запит basket_data/pay у роботі - кнопка «Відправити» заблокована.
  // Лоадер перекриває екран тільки для миші, а з клавіатури (Enter/пробіл)
  // сфокусована кнопка відправляла запит повторно.
  isSendingPayment = false;
  // Блок «Попереднє замовлення ще обробляється…» над кнопкою «Відправити»
  showOrderInProgressMessage = false;
  selectedPlaceId: string | null = null;
  // Текст адреси, до якого належить selectedPlaceId (підказка Google або адреса з минулого замовлення).
  selectedPlaceAddress: string | null = null;
  protected notify = inject(HttpErrorNotifyService);
  // Опції запиту basket_data/pay: коди редиректу pay() розбирає сам (PAY_HANDLED_ERRORS), тож
  // інтерцептор віддає їх, а не обриває запит - інакше finalize показав би «статус невідомий»,
  // хоча на ці коди бек відмовляє ще до створення замовлення
  protected payRequestOptions = {context: new HttpContext().set(PASS_REDIRECT_RESPONSE, true)};

  constructor(
    public http: HttpClient,
    public localStorage: LocalStorageService,
  ) {
    super();
  }

  ngOnInit() {
    this.getData();
  }

  getData() {
    this.delivery = parseInt(this.localStorage.get('delivery_id'));

    this.http.post(this.apiRoot + 'basket_data/delivery_type', {
      currency: this.localStorage.get('currencies'),
      delivery_id: this.delivery
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(async (data: BasketConfirm) => {
        if (data.error == 'empty basket') {
          window.location.href = '/account'
          return;
        }

        this.deliveryCurrent = data.del_current;
        this.user = data.user;
        this.lastAddress = data.last_address;

        if (this.lastAddress && this.lastAddress.google_place_id !== null) {
          this.selectedPlaceId = this.lastAddress.google_place_id;
          this.selectedPlaceAddress = this.lastAddress.city;
        }

        if (this.delivery === 1 && this.selectedPlaceId === null && this.lastAddress) {
          this.lastAddress.city = '';
        }

        if (data.del_current.delivery_dates.includes(data.dozakaz_date)) {
            this.dozakazDate = data.dozakaz_date;
        }

        this.separateOrders = data.separate_orders;
        this.addressedDelivery = data.addressed_delivery;
        this.summaUah = data.summa_uah;
        const declaredPriceBase = data.summa_uah < 300 ? 300 : data.summa_uah;
        this.declaredPrice = declaredPriceBase;
        this.liters = data.liters;
        this.compensation = data.compensation;
        this.deliveryCities = data.delivery_cities;
        this.deliveryList = data.delivery_info;
        this.wars = data.wars;

        // Наценки за наложку может не быть (nalojka_markup = 0 / null / не пришло) —
        // тогда сумма послеоплаты равна обычной сумме заказа. Без этой ветки
        // summaUahWithMarkup оставался undefined и sumMarkupNalojka ниже
        // превращался в NaN.
        const nalojkaMarkup = Number(this.user.nalojka_markup) || 0;
        this.summaUahWithMarkup = nalojkaMarkup > 0
          ? Math.ceil(this.summaUah + (this.summaUah / 100 * nalojkaMarkup))
          : this.summaUah;

        if (this.liters < 5) {
          this.declaredPrice = declaredPriceBase + 20;
          this.sumMarkupNalojka = this.summaUahWithMarkup + 20;
        } else if (this.liters >= 5 && this.liters < 10) {
          this.declaredPrice = declaredPriceBase + 25;
          this.sumMarkupNalojka = this.summaUahWithMarkup + 25;
        } else {
          this.declaredPrice = declaredPriceBase;
          this.sumMarkupNalojka = this.summaUahWithMarkup
        }

        if (this.user.dels_settings && this.user.dels_settings.status_otgrujaem) {
          this.showMessageOtgrujaem = this.user.dels_settings.status_otgrujaem[this.delivery] || 0;
        }

        if (this.lastAddress && this.lastAddress.fio) {
          let fio = this.lastAddress.fio.split(' ');

          this.name = fio[1];
          this.lastName = fio[0];
          // Прізвище та ім'я пробілів не мають (RECIPIENT_PART_PATTERN), а по батькові може бути
          // складеним («Алім огли») - беремо весь хвіст, інакше наступне замовлення тихо йшло б
          // з урізаним ПІБ одержувача
          this.middleName = fio.slice(2).join(' ');
        }

        this.validationPhone();

        this.validate();
      });
  }

  validate() {

  }

  validationPhone() {
    if (this.lastAddress == null) {
      this.lastAddress = {
        np_addr_street: '',
        np_addr_house: '',
        np_addr_flat: '',
        city: '',
        edrpou: '',
        organization: '',
      };
    }

    if (!this.lastAddress.phone) {
      this.lastAddress.phone = this.user.phone.toString().replace(/^\+?38/, '');
    } else {
      this.lastAddress.phone = '0' + this.lastAddress.phone;
    }

    setTimeout(() => {
      const evt = new Event("input");
      if (this.phoneRef && this.phoneRef.nativeElement) {
        this.phoneRef.nativeElement.dispatchEvent(evt);
      }
    }, 10)
  }

  inputLastAddressPhone() {
    const phone: string = this.phoneRef.nativeElement.value.replace(/\+38/g, '').replace(/\D/g, '');

    if (!/^\d{10}$/.test(phone)) {
      this.wrongLastAddressPhone = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongLastAddressPhone = false;
    }
  }

  crossClearPhone() {
    this.lastAddress.phone = '';
    this.phoneRef.nativeElement.value = '';
    this.inputLastAddressPhone();
  }

  changingValueDeliverySeparate() {
    if (this.deliverySeparate == -1) {
      this.wrongDeliverySeparate = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongDeliverySeparate = false;
    }
  }

  reserveInfoMessage() {
    this.reserveInfoMessageShow = !this.reserveInfoMessageShow;
  }

  inputEstimateOfTheCostOfTheOrder() {
    if (!this.declaredPrice) {
      this.wrongEstimateOfTheCostOfTheOrder = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongEstimateOfTheCostOfTheOrder = false;
    }
  }

  inputLastAddressEdrpou() {
    // Поле текстове: з type="number" Angular перетворював код на число і губив провідні нулі
    // (00480951 → 480951 зберігалось в адресу). Лишаємо тільки цифри, як і дозволяло числове поле
    if (this.lastAddress.edrpou !== null && this.lastAddress.edrpou !== undefined) {
      this.lastAddress.edrpou = String(this.lastAddress.edrpou).replace(/\D/g, '');
    }

    if (!this.lastAddress.edrpou || this.lastAddress.edrpou.length == 0) {
      this.wrongLastAddressEdrpou = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongLastAddressEdrpou = false;
    }
  }

  // Алфавіт частини ПІБ: для НП — тільки кирилиця, для решти доставок — як було
  recipientNamePattern(): RegExp {
    return this.recipientNameCyrillicOnly ? RECIPIENT_PART_PATTERN : RECIPIENT_PART_PATTERN_ANY;
  }

  inputLastName() {
    // Убираем пробелы при вставке
    this.lastName = (this.lastName ?? '').toString().trim().replace(/\s+/g, ' ');
    // Разрешаем дифис внутри двойной фамилии
    this.lastName = this.lastName.replace(/\s*-\s*/g, '-');

    if (this.lastName === '') {
      this.wrongLastName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageLastName = 'Введіть прізвище';
    } else if (this.recipientNameCyrillicOnly && RECIPIENT_LATIN_PATTERN.test(this.lastName)) {
      this.wrongLastName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageLastName = 'Латинські літери не допускаються — лише кирилиця';
    } else if (!this.recipientNamePattern().test(this.lastName)) {
      this.wrongLastName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageLastName = 'Некоректне прізвище';
    } else {
      this.wrongLastName = false;
    }
  }

  inputName() {
    // Убираем пробелы при вставке
    this.name = (this.name ?? '').toString().trim().replace(/\s+/g, ' ');

    if (this.name === '') {
      this.wrongName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageName = 'Введіть ім\'я';
    } else if (this.recipientNameCyrillicOnly && RECIPIENT_LATIN_PATTERN.test(this.name)) {
      this.wrongName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageName = 'Латинські літери не допускаються — лише кирилиця';
    } else if (!this.recipientNamePattern().test(this.name)) {
      this.wrongName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageName = 'Некоректне ім\'я';
    } else {
      this.wrongName = false;
    }
  }

  // По батькові необов'язкове, але так само їде у ПІБ одержувача, тож для НП
  // перевіряємо за тими самими правилами. Заразом нормалізує undefined
  // (у lastAddress.fio могло не бути третього слова) — інакше у ПІБ
  // потрапляло б латинське слово «undefined».
  inputMiddleName() {
    this.middleName = (this.middleName ?? '').toString().trim().replace(/\s+/g, ' ');

    if (this.middleName === '' || !this.recipientNameCyrillicOnly) {
      this.wrongMiddleName = false;
    } else if (RECIPIENT_LATIN_PATTERN.test(this.middleName)) {
      this.wrongMiddleName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageMiddleName = 'Латинські літери не допускаються — лише кирилиця';
    } else if (!RECIPIENT_MIDDLE_PATTERN.test(this.middleName)) {
      this.wrongMiddleName = true;
      this.isValidDeliveryForm = true;
      this.errorMessageMiddleName = 'Некоректне по батькові';
    } else {
      this.wrongMiddleName = false;
    }
  }

  crossClearLastName() {
    this.lastName = '';
    this.inputLastName();
  }

  crossClearName() {
    this.name = '';
    this.inputName();
  }

  crossClearMiddleName() {
    this.middleName = '';
    this.inputMiddleName();
  }

  crossClearAddressEdrpou() {
    this.lastAddress.edrpou = '';
    this.inputLastAddressEdrpou();
  }

  crossClearPrice() {
    this.declaredPrice = '';
    this.inputEstimateOfTheCostOfTheOrder();
  }

  validateSendPayment() {

  }

  resultSendPayment(data: OrdersSuccessData) {

  }

  scrollToInvalidForm() {

  }

  // Відповідь бекенду про вже запущене оформлення. Повертає true, якщо це саме
  // той випадок і далі відповідь розбирати не треба.
  orderInProgress(data): boolean {
    if (data && data.error === ORDER_IN_PROGRESS_ERROR) {
      this.showOrderInProgressMessage = true;
      return true;
    }

    return false;
  }

  // Помилка pay(), яку не розібрав екран доставки: показуємо власний текст
  // за кодом (сирий data.error користувачу не віддаємо), різні причини - окремо.
  notifyPayError(data): void {
    if (!data || typeof data.error !== 'string' || PAY_HANDLED_ERRORS.includes(data.error)) {
      return;
    }

    const error: string = data.error;
    let text = 'Перевірте дані замовлення і спробуйте ще раз. Якщо помилка повториться - зверніться до менеджера.';

    if (error === 'wrong delivery date') {
      text = 'Обрана дата відправки вже недоступна. Оберіть іншу дату і відправте замовлення ще раз.';
    } else if (error.startsWith('Данные в корзине были автоматически откорректированы')) {
      text = 'Залишки або ціни змінились, і кошик скориговано автоматично. Перевірте кошик і оформіть замовлення ще раз.';
    } else if (error.startsWith('Білий облік тимчасово вимкнено')) {
      text = 'Оформлення замовлень по договору тимчасово недоступне.';
    } else if (PAY_PHONE_ERRORS.includes(error)) {
      text = 'Перевірте номер телефону отримувача.';
    } else if (PAY_FIO_ERRORS.includes(error)) {
      text = 'Перевірте ПІБ отримувача.';
    } else if (error === 'empty edrpou') {
      text = 'Вкажіть ЄДРПОУ організації.';
    } else if (error === 'wrong delivery address' && PAY_LONG_FIELD_LABELS[data.data?.field]) {
      // Бек відхиляє задовге поле адреси (#471) і називає його в data.field - вибір зі списку
      // підказок тут не допоможе, клієнт надсилав ту саму адресу знову
      text = `Поле «${PAY_LONG_FIELD_LABELS[data.data.field]}» - не більше ${data.data.max_length} символів. Скоротіть його і відправте замовлення ще раз.`;
    } else if (PAY_ADDRESS_ERRORS.includes(error)) {
      text = 'Перевірте місто та адресу або відділення доставки - оберіть їх зі списку підказок.';
    } else if (error === 'dropship files required') {
      // Дропшип за новою схемою (#867): тимчасові файли не знайдено - бек зберігає їх добу
      text = 'Прикріпіть наклейку і реєстр ще раз: завантажені файли не знайдено або минула доба після завантаження.';
    } else if (error === 'dropship places mismatch' && data.data) {
      // Дропшип (#868): місця з наклейки не збігаються з розрахунком по кошику - клієнт перезавантажує наклейку
      text = dropshipPlacesMismatchText(Number(data.data.label), Number(data.data.calc));
    } else if (error === 'wrong dropship ttn') {
      text = DROPSHIP_TTN_TOO_LONG;
    }

    this.notify.show('Замовлення не оформлено', text);
  }

  // pay() створює замовлення ДО відповіді (кошик чистить уже після echo), тож обрив зв'язку,
  // 5xx/504 або скасування запиту лоадером не означають «не оформлено». Інтерцептор при цьому
  // радив «повторіть дію» - і повтор після зняття lock створював дубль замовлення.
  // 4xx (зокрема 404) до створення замовлення не доходять - там лишаємо текст інтерцептора.
  notifyPayUnknown(response?: HttpErrorResponse): void {
    if (response instanceof HttpErrorResponse && response.status >= 400 && response.status < 500) {
      return;
    }

    this.notify.show(
      'Статус замовлення невідомий',
      'Замовлення могло бути оформлене. Перш ніж відправляти ще раз, перевірте розділ «Замовлення».'
    );
  }

  sendPayment() {
    if (this.isSendingPayment) {
      return;
    }

    this.showOrderInProgressMessage = false;

    const sendData = this.validateSendPayment();

    if (!this.isValidDeliveryForm) {
      this.isSendingPayment = true;
      let isAnswered = false;

      this.http.post(this.apiRoot + 'basket_data/pay', sendData, this.payRequestOptions)
        .pipe(
          takeUntil(this.isDestroyed),
          tap(() => isAnswered = true),
          catchError((response) => {
            isAnswered = true;
            this.notifyPayUnknown(response);

            return EMPTY;
          }),
          // finalize, а не сам subscribe: скасування лоадером і помилка HTTP
          // теж мають розблокувати кнопку
          finalize(() => {
            this.isSendingPayment = false;

            // відповіді не було (скасування лоадером, вихід зі сторінки) - статус невідомий
            if (!isAnswered) {
              this.notifyPayUnknown();
            }
          })
        )
        .subscribe((data: OrdersSuccessData) => {
          if (this.orderInProgress(data)) {
            return;
          }

          this.resultSendPayment(data);
          this.notifyPayError(data);
        });
    } else {
      this.scrollToInvalidForm();
    }
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
